import { useState, useEffect, useRef } from "react";
import { Layout, SectionLabel, PageHero } from "../components.jsx";
import { navy, blue, INDUSTRIES } from "../data";

const CALENDLY_URL   = "https://calendly.com/pankajp1/30min";
const CALENDLY_EMBED = "https://calendly.com/pankajp1/30min?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=2563eb&text_color=0f1e3c&background_color=ffffff";

const TRUST = [
  { text: "30-minute focused call" },
  { text: "No obligation" },
  { text: "Practical automation insights" },
];

const NEXT_STEPS = [
  { num: "01", title: "We Review Your Submission",    desc: "We read every enquiry personally and prepare context for your specific industry and challenge." },
  { num: "02", title: "We Reach Out Within 1 Day",    desc: "You'll hear from us within 1 business day to confirm details and align on your goals." },
  { num: "03", title: "Free 30-Minute Strategy Call", desc: "A focused call to understand your operations and identify where AI can create the most value." },
];

const POPULAR_TIMES = ["Mon 9:00am", "Mon 2:00pm", "Tue 10:00am", "Wed 11:00am", "Thu 9:00am", "Fri 10:00am"];

const CREDIBILITY = [
  "AI automation for operations-heavy businesses",
  "Practical systems, not theoretical AI strategy",
  "Focused on measurable efficiency gains",
];

// ── Reliable Calendly inline widget ──────────────────────────────────────────
// Strategy: inject script, wait for it to load, THEN call initInlineWidget
// on the already-mounted ref div. This guarantees the widget renders in React.
function CalendlyWidget() {
  const containerRef = useRef(null);

  useEffect(() => {
    function initWidget() {
      if (window.Calendly && containerRef.current) {
        // Clear any previous attempt
        containerRef.current.innerHTML = "";
        window.Calendly.initInlineWidget({
          url: CALENDLY_EMBED,
          parentElement: containerRef.current,
          prefill: {},
          utm: {},
        });
      }
    }

    // If script already loaded (e.g. navigating back), init immediately
    if (window.Calendly) {
      initWidget();
      return;
    }

    // Load CSS once
    if (!document.getElementById("calendly-css")) {
      const link = document.createElement("link");
      link.id   = "calendly-css";
      link.rel  = "stylesheet";
      link.href = "https://assets.calendly.com/assets/external/widget.css";
      document.head.appendChild(link);
    }

    // Load script once, then init on load
    if (!document.getElementById("calendly-script")) {
      const script    = document.createElement("script");
      script.id       = "calendly-script";
      script.src      = "https://assets.calendly.com/assets/external/widget.js";
      script.async    = true;
      script.onload   = initWidget;
      document.body.appendChild(script);
    } else {
      // Script tag exists but may still be loading — poll until ready
      const poll = setInterval(() => {
        if (window.Calendly) {
          clearInterval(poll);
          initWidget();
        }
      }, 100);
      return () => clearInterval(poll);
    }
  }, []);

  return (
    <div
      ref={containerRef}
      style={{ minWidth: 300, height: 730, width: "100%" }}
    />
  );
}

export default function Contact() {
  const [activeTab, setActiveTab] = useState("book");

  return (
    <Layout>
      <PageHero
        label="Get In Touch"
        title="Let's Talk About Your Business"
        subtitle="Book a free strategy call directly, or send us a message and we'll be in touch within 1 business day."
      />

      <section className="py-12 bg-gray-50">
        <div className="max-w-6xl mx-auto px-6">

          {/* Section header + trust indicators */}
          <div className="text-center mb-6">
            <SectionLabel>Schedule a Call</SectionLabel>
            <h2 className="text-2xl md:text-3xl font-bold mb-3" style={{ color: navy }}>
              Schedule Your AI Strategy Call
            </h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto leading-relaxed">
              Choose a time that works for you. We'll review your operations and identify where AI automation can create the most impact.
            </p>
            <div className="flex flex-wrap justify-center gap-6 mt-5">
              {TRUST.map(t => (
                <div key={t.text} className="flex items-center gap-2">
                  <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background: blue }}>✓</span>
                  <span className="text-gray-600 text-sm font-medium">{t.text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-2 mb-10 bg-white border border-gray-200 rounded-lg p-1 w-fit mx-auto shadow-sm">
            <button
              onClick={() => setActiveTab("book")}
              className="px-6 py-2.5 rounded-md text-sm font-semibold transition-all duration-200"
              style={{ background: activeTab === "book" ? navy : "transparent", color: activeTab === "book" ? "#fff" : "#64748b" }}
            >
              📅 Book a Free Strategy Call
            </button>
            <button
              onClick={() => setActiveTab("message")}
              className="px-6 py-2.5 rounded-md text-sm font-semibold transition-all duration-200"
              style={{ background: activeTab === "message" ? navy : "transparent", color: activeTab === "message" ? "#fff" : "#64748b" }}
            >
              ✉️ Send a Message
            </button>
          </div>

          {/* ── BOOK TAB ─────────────────────────────────────────────────────── */}
          {activeTab === "book" && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">

              {/* Left col — 60% */}
              <div className="lg:col-span-3 flex flex-col gap-4">

                {/* Popular times */}
                <div className="bg-white rounded-xl border border-gray-100 px-5 py-4" style={{ boxShadow: "0 2px 10px rgba(15,30,60,0.06)" }}>
                  <p className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: blue }}>Popular Booking Times</p>
                  <div className="flex flex-wrap gap-2">
                    {POPULAR_TIMES.map(slot => (
                      <span key={slot} className="px-3 py-1.5 rounded-md text-xs font-semibold border" style={{ background: "#f0f7ff", color: navy, borderColor: "#bfdbfe" }}>
                        {slot}
                      </span>
                    ))}
                  </div>
                </div>

                {/* Calendly inline widget */}
                <div className="bg-white rounded-xl border border-gray-100 overflow-hidden" style={{ boxShadow: "0 4px 24px rgba(15,30,60,0.08)" }}>
                  <CalendlyWidget />
                </div>

                {/* Credibility strip */}
                <div className="rounded-xl border border-gray-100 px-6 py-5" style={{ background: `linear-gradient(135deg, ${navy} 0%, #1a3260 100%)` }}>
                  <p className="text-white font-bold text-sm mb-4">Why Businesses Work With VedaLogic</p>
                  <div className="space-y-3">
                    {CREDIBILITY.map(point => (
                      <div key={point} className="flex items-start gap-3">
                        <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{ background: "#2563eb", color: "#fff" }}>✓</span>
                        <span className="text-blue-100 text-sm leading-relaxed">{point}</span>
                      </div>
                    ))}
                  </div>
                </div>

              </div>

              {/* Right col — 40% */}
              <div className="lg:col-span-2 flex flex-col gap-5 lg:pt-2">

                <div className="rounded-xl p-6 border border-blue-100" style={{ background: "#f0f7ff" }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">📞</span>
                    <h3 className="font-bold text-base" style={{ color: navy }}>What to Expect</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    A <strong>free 30-minute call</strong> to review your operations, identify your biggest AI opportunities, and give you a clear picture of what's possible — with no obligation.
                  </p>
                </div>

                <div className="rounded-xl p-6 bg-white border border-gray-100" style={{ boxShadow: "0 2px 12px rgba(15,30,60,0.06)" }}>
                  <h3 className="font-bold text-base mb-5" style={{ color: navy }}>What Happens Next?</h3>
                  <div className="space-y-5">
                    {NEXT_STEPS.map(step => (
                      <div key={step.num} className="flex items-start gap-4">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background: blue }}>
                          {step.num}
                        </div>
                        <div>
                          <p className="font-semibold text-sm mb-1" style={{ color: navy }}>{step.title}</p>
                          <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl p-6 bg-white border border-gray-100" style={{ boxShadow: "0 2px 12px rgba(15,30,60,0.06)" }}>
                  <h3 className="font-bold text-sm mb-2" style={{ color: navy }}>Prefer to Connect Directly?</h3>
                  <p className="text-gray-500 text-sm mb-3 leading-relaxed">Find us on LinkedIn for updates, insights, and direct messaging.</p>
                  <a href="https://linkedin.com/company/vedalogic" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold hover:underline">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="#0A66C2"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                    Connect on LinkedIn →
                  </a>
                </div>

              </div>
            </div>
          )}

          {/* ── MESSAGE TAB ──────────────────────────────────────────────────── */}
          {activeTab === "message" && (
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">

              {/* Left: contact form — 60% */}
              <div className="lg:col-span-3">
                <div className="bg-white rounded-xl border border-gray-100 p-8" style={{ boxShadow: "0 4px 24px rgba(15,30,60,0.08)" }}>
                  <h2 className="text-xl font-bold mb-6" style={{ color: navy }}>Send Us a Message</h2>
                  <form className="space-y-5" onSubmit={e => { e.preventDefault(); alert("Thank you! We'll be in touch within 1 business day."); }}>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Your Name</label>
                        <input type="text" required placeholder="Jane Smith" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors bg-white" />
                      </div>
                      <div>
                        <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Company Name</label>
                        <input type="text" required placeholder="Acme Landscaping" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors bg-white" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Email Address</label>
                      <input type="email" required placeholder="jane@company.com" className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors bg-white" />
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Industry</label>
                      <select className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors text-gray-700 bg-white">
                        <option value="">Select your industry</option>
                        {INDUSTRIES.map(ind => <option key={ind.name}>{ind.name}</option>)}
                        <option>Other</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-bold uppercase tracking-widest text-gray-500 mb-2">Biggest Operational Challenge</label>
                      <textarea rows={5} required placeholder="e.g. We spend too much time on manual follow-ups and our team struggles to keep up with inbound enquiries..." className="w-full border border-gray-200 rounded-lg px-4 py-3 text-sm focus:outline-none focus:border-blue-500 transition-colors resize-none bg-white" />
                    </div>
                    <div className="pt-1">
                      <button type="submit" className="w-full py-4 font-bold text-white rounded-lg text-sm uppercase tracking-widest transition-all duration-200 hover:opacity-90" style={{ background: navy }}>
                        Send Message — We Respond Within 1 Business Day
                      </button>
                    </div>
                  </form>
                </div>
              </div>

              {/* Right: info cards — 40% */}
              <div className="lg:col-span-2 flex flex-col gap-5 lg:pt-2">

                <div className="rounded-xl p-6 border border-blue-100" style={{ background: "#f0f7ff" }}>
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-lg">⏱</span>
                    <h3 className="font-bold text-base" style={{ color: navy }}>Response Time</h3>
                  </div>
                  <p className="text-gray-600 text-sm leading-relaxed">
                    We respond to every enquiry within <strong>1 business day</strong>. For urgent requests, mention it in your message and we'll prioritise accordingly.
                  </p>
                </div>

                <div className="rounded-xl p-6 bg-white border border-gray-100" style={{ boxShadow: "0 2px 12px rgba(15,30,60,0.06)" }}>
                  <h3 className="font-bold text-base mb-5" style={{ color: navy }}>What Happens Next?</h3>
                  <div className="space-y-5">
                    {NEXT_STEPS.map(step => (
                      <div key={step.num} className="flex items-start gap-4">
                        <div className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 text-xs font-bold text-white" style={{ background: blue }}>
                          {step.num}
                        </div>
                        <div>
                          <p className="font-semibold text-sm mb-1" style={{ color: navy }}>{step.title}</p>
                          <p className="text-gray-500 text-sm leading-relaxed">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="rounded-xl p-6 bg-white border border-gray-100" style={{ boxShadow: "0 2px 12px rgba(15,30,60,0.06)" }}>
                  <h3 className="font-bold text-sm mb-2" style={{ color: navy }}>Prefer to Book Directly?</h3>
                  <p className="text-gray-500 text-sm mb-3 leading-relaxed">Skip the form and book a free strategy call at a time that suits you.</p>
                  <button onClick={() => setActiveTab("book")} className="text-blue-600 text-sm font-semibold hover:underline">
                    Book a Free Strategy Call →
                  </button>
                </div>

              </div>
            </div>
          )}

        </div>
      </section>
    </Layout>
  );
}